import type { FC } from "react";
import { useEffect, useId } from "react";
import { useFormContext } from "react-hook-form";

import { useTranslations } from "@ui/providers/useTranslations";

import type { Gender } from "@jti/fe-common";

import type { InputWrapperProps } from "../Inputs";
import { Select } from "../Inputs";

type GenderDropdownProps = Pick<
  InputWrapperProps,
  | "label"
  | "helperText"
  | "isHelperTextVisible"
  | "isError"
  | "errorText"
  | "isOptional"
  | "containerClassName"
  | "wrapperClassName"
  | "showRequiredAsterisk"
> & {
  fieldName: string;
  "aria-label"?: string;
  isDisabled?: boolean;
  required?: boolean;
};

export const GenderDropdown: FC<GenderDropdownProps> = ({
  fieldName,
  "aria-label": ariaLabel,
  isDisabled = false,
  label,
  required,
  ...props
}) => {
  const { __ } = useTranslations();
  const { register } = useFormContext();
  const generatedId = useId();
  const dropdownId = `genderDropdown-${generatedId}`;
  const dropdownValues: Array<{ value: Gender; label: string }> = [
    { value: "male", label: __("contact_info.gender.male") },
    { value: "female", label: __("contact_info.gender.female") },
    { value: "prefer_not_to_say", label: __("contact_info.gender.prefer_not_to_say") },
  ];

  useEffect(() => {
    register(fieldName);
  }, [fieldName, register]);

  return (
    <Select
      {...props}
      dataTestId="genderDropdown"
      id={dropdownId}
      name={fieldName}
      aria-label={ariaLabel}
      isSearchable={false}
      placeholder={__("contact_info.gender_placeholder")}
      options={dropdownValues}
      isDisabled={isDisabled}
      label={label}
      required={required}
    />
  );
};
